import { useState, useEffect } from 'react';
import { 
  MessageSquareOff, MessageSquare, Search, Phone, User, Calendar,
  RefreshCw, CheckCircle, XCircle, AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '../../../utils/apiClient';

/**
 * SMS Opt-Out Management Tab for Admin Panel
 * Shows customers who have opted out of SMS and allows manual management
 */
export default function SmsOptOutTab({ admin }) {
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [optOutLog, setOptOutLog] = useState([]);
  const [checkPhone, setCheckPhone] = useState('');
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    fetchOptOutLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOptOutLog = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/sms-activity');
      setOptOutLog(res.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch SMS log:', err);
    }
    setLoading(false);
  };

  const checkOptStatus = async () => {
    if (!checkPhone.trim()) {
      toast.error('Please enter a phone number');
      return;
    }
    
    setChecking(true);
    setCheckResult(null);
    try {
      const res = await api.get(`/sms/opt-status/${encodeURIComponent(checkPhone.trim())}`);
      setCheckResult(res.data);
    } catch (err) {
      toast.error('Error checking status');
    }
    setChecking(false);
  };

  const toggleOptStatus = async (phone, currentStatus) => {
    const endpoint = currentStatus ? 'opt-in' : 'opt-out';
    try {
      const res = await api.post(`/sms/${endpoint}/${encodeURIComponent(phone)}`, { reason: 'admin_manual' });
      
      toast.success(currentStatus ? 'Customer re-subscribed to SMS' : 'Customer opted out of SMS');
      setCheckResult(null);
      setCheckPhone('');
      fetchOptOutLog();
    } catch (err) {
      toast.error('Error updating status');
    }
  };

  const filteredLog = optOutLog.filter(log => 
    log.from?.includes(searchQuery) || 
    log.body?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.action?.includes(searchQuery)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">SMS Opt-Out Management</h2>
          <p className="text-muted-foreground mt-1">
            Manage customer SMS preferences. Customers can reply STOP to unsubscribe.
          </p>
        </div>
        <Button variant="outline" onClick={fetchOptOutLog}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Check Phone Status */}
      <div className="bg-card rounded-xl border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Phone className="w-5 h-5 text-primary" />
          Check Customer SMS Status
        </h3>
        <div className="flex gap-3">
          <div className="relative flex-1 max-w-md">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Enter phone number (e.g., 6791234567)"
              value={checkPhone}
              onChange={(e) => setCheckPhone(e.target.value)}
              className="pl-10"
              onKeyDown={(e) => e.key === 'Enter' && checkOptStatus()}
            />
          </div>
          <Button onClick={checkOptStatus} disabled={checking}>
            {checking ? 'Checking...' : 'Check Status'}
          </Button>
        </div>
        
        {/* Check Result */}
        {checkResult && (
          <div className={`mt-4 p-4 rounded-lg border-2 ${
            checkResult.sms_opted_out 
              ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' 
              : 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {checkResult.sms_opted_out ? (
                  <MessageSquareOff className="w-8 h-8 text-red-500" />
                ) : (
                  <MessageSquare className="w-8 h-8 text-green-500" />
                )}
                <div>
                  <p className="font-semibold text-foreground">
                    {checkResult.phone}
                  </p>
                  <p className={`text-sm ${checkResult.sms_opted_out ? 'text-red-600' : 'text-green-600'}`}>
                    {checkResult.sms_opted_out ? 'OPTED OUT - Will not receive SMS' : 'SUBSCRIBED - Can receive SMS'}
                  </p>
                  {checkResult.updated_at && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Last updated: {new Date(checkResult.updated_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
              <Button
                variant={checkResult.sms_opted_out ? 'default' : 'destructive'}
                size="sm"
                onClick={() => toggleOptStatus(checkResult.phone, checkResult.sms_opted_out)}
              >
                {checkResult.sms_opted_out ? 'Re-subscribe' : 'Opt Out'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* How It Works */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
        <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-3">
          How SMS Opt-Out Works
        </h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold flex-shrink-0">1</div>
            <div>
              <p className="font-medium text-blue-800 dark:text-blue-200">Customer Replies STOP</p>
              <p className="text-blue-600 dark:text-blue-300">To any SMS from Bulaccino</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold flex-shrink-0">2</div>
            <div>
              <p className="font-medium text-blue-800 dark:text-blue-200">System Tags Them</p>
              <p className="text-blue-600 dark:text-blue-300">Marked as opted-out in database</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold flex-shrink-0">3</div>
            <div>
              <p className="font-medium text-blue-800 dark:text-blue-200">No More SMS</p>
              <p className="text-blue-600 dark:text-blue-300">But they stay in loyalty program!</p>
            </div>
          </div>
        </div>
        <p className="mt-4 text-sm text-blue-700 dark:text-blue-300">
          <strong>To re-subscribe:</strong> Customer replies START or you can manually re-subscribe them above.
        </p>
      </div>

      {/* Activity Log */}
      <div className="bg-card rounded-xl border overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold">Recent SMS Activity</h3>
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9"
            />
          </div>
        </div>
        
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">
            <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin" />
            Loading...
          </div>
        ) : filteredLog.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>No SMS activity recorded yet</p>
            <p className="text-sm mt-1">Activity will appear here when customers reply to SMS</p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredLog.slice(0, 50).map((log, idx) => (
              <div key={idx} className="p-4 hover:bg-muted/30 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      log.action === 'opt_out' ? 'bg-red-100 text-red-600' :
                      log.action === 'opt_in' ? 'bg-green-100 text-green-600' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {log.action === 'opt_out' ? <MessageSquareOff className="w-5 h-5" /> :
                       log.action === 'opt_in' ? <MessageSquare className="w-5 h-5" /> :
                       <AlertTriangle className="w-5 h-5" />}
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{log.from}</p>
                      <p className="text-sm text-muted-foreground">"{log.body}"</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(log.received_at).toLocaleString()}
                        </span>
                        {log.action && (
                          <span className={`px-2 py-0.5 rounded-full ${
                            log.action === 'opt_out' ? 'bg-red-100 text-red-700' :
                            log.action === 'opt_in' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {log.action.replace('_', ' ').toUpperCase()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {log.action === 'opt_out' && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => toggleOptStatus(log.from, true)}
                    >
                      Re-subscribe
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Twilio Setup Instructions */}
      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-6 border border-amber-200 dark:border-amber-800">
        <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200 mb-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Twilio Webhook Setup Required
        </h3>
        <p className="text-amber-700 dark:text-amber-300 mb-3">
          To receive STOP/START messages, configure this webhook URL in your Twilio Console:
        </p>
        <code className="block bg-white dark:bg-gray-800 p-3 rounded-lg text-sm font-mono break-all border">
          {window.location.origin.replace('preview.', '')}/api/sms/webhook/incoming
        </code>
        <p className="text-sm text-amber-600 dark:text-amber-400 mt-3">
          Go to Twilio Console → Phone Numbers → Your Number → Messaging → "A MESSAGE COMES IN" → Set to Webhook with this URL
        </p>
      </div>
    </div>
  );
}
